// A tabbed page remembers its active tab — per page, per user — across refresh
// and navigation. ONE mechanism for every strip (Settings, Reports, Commissions,
// Attendance, EOI, Deals), so the rule can be executed by a gate
// (verify-tab-memory.js) instead of read in six places.
//
// WHY (owner, 2026-10-28). Every strip returned to its first tab on refresh.
// Reports was the only page that stored its tab (`crm_reports_tab`, no user in
// the key); Settings, Commissions, EOI and Deals started from a hard-coded id
// (`useState("general")` …) and Attendance from a capability-ranked default —
// in-memory only, gone with the page. Nothing was being overridden by the
// visibility matrix: the pages mount only after the envelope has landed
// (renderPage holds on the dashboard until accessLoaded) and resolveTabStrip
// derives the SHOWN tab from the stored one at render time. There was simply
// nothing stored to restore.
//
// THE ORDER, in initialTab, pure:
//   1. a deep link (a prop such as Attendance's initTab, or a one-shot request
//      left by a navigation — the report drill-down's "← Back to report");
//   2. the stored tab for (page, user);
//   3. the page's fallback — its first tab.
// Each step applies only when that id is in the page's own registry; an id
// that is not (renamed, removed) falls through. VISIBILITY IS NOT DECIDED
// HERE: the strip still goes through resolveTabStrip, which shows vis[0]
// whenever the remembered tab is hidden for this role — and the remembered
// value is left alone, so it comes back the day the role regains the tab.
// Attendance hands in its allowed tabs as the registry (its tabs are the
// four capabilities, not surfaces), which is the same rule one layer earlier.
//
// Writes happen through the setter the hook returns — the chip click, the
// landers, the drill-downs — so there is one write site per page. The third
// element, setTabQuiet, changes the tab WITHOUT remembering it: Attendance's
// permission-revoke fallback uses it, so a revoked tab is not overwritten by
// the stand-in. localStorage reads and writes are wrapped; a blocked store
// simply means the page opens at its fallback.
import { useState, useCallback } from "react";

export var TAB_KEY_PREFIX = "crm_tab:";
export var TAB_REQUEST_PREFIX = "crm_tab_request:";

export function tabMemoryKey(pageId, userId) {
  return TAB_KEY_PREFIX + String(pageId || "") + ":" + String(userId || "anon");
}
export function readStoredTab(pageId, userId) {
  try { return localStorage.getItem(tabMemoryKey(pageId, userId)) || null; } catch (e) { return null; }
}
export function writeStoredTab(pageId, userId, tabId) {
  try { if (tabId) localStorage.setItem(tabMemoryKey(pageId, userId), String(tabId)); } catch (e) {}
}
// A one-shot deep-link intent for the NEXT mount of `pageId` (no user id: the
// navigation that leaves it is the same session that consumes it).
export function requestTab(pageId, tabId) {
  try { if (tabId) localStorage.setItem(TAB_REQUEST_PREFIX + String(pageId || ""), String(tabId)); } catch (e) {}
}
export function takeRequestedTab(pageId) {
  try {
    var k = TAB_REQUEST_PREFIX + String(pageId || "");
    var v = localStorage.getItem(k);
    if (v) localStorage.removeItem(k);
    return v || null;
  } catch (e) { return null; }
}

function idsOf(list) {
  return (list || []).map(function (t) { return t && typeof t === "object" ? t.id : t; }).filter(Boolean);
}

// PURE. { list, deepLink, stored, fallback } → the tab to open.
export function initialTab(o) {
  var ids = idsOf(o && o.list);
  var has = function (id) { return !!id && ids.indexOf(id) >= 0; };
  if (has(o.deepLink)) return o.deepLink;
  if (has(o.stored)) return o.stored;
  if (has(o.fallback)) return o.fallback;
  return ids.length ? ids[0] : (o.fallback || null);
}

// [tab, setTab (remembers), setTabQuiet (does not)].
export function useRememberedTab(pageId, userId, list, deepLink, fallback) {
  var pair = useState(function () {
    return initialTab({ list: list, deepLink: deepLink || takeRequestedTab(pageId), stored: readStoredTab(pageId, userId), fallback: fallback });
  });
  var tab = pair[0], setState = pair[1];
  var setTab = useCallback(function (id) { setState(id); writeStoredTab(pageId, userId, id); }, [pageId, userId, setState]);
  return [tab, setTab, setState];
}
